/**
 * toggleTaskCompletionWithRecurrence.js
 *
 * Use-case:
 * - toggles completion for one task
 * - when recurring and toggling to done, computes and creates the next occurrence
 *
 * This keeps business decisions out of UI components and keeps taskService as
 * an event boundary instead of a rule engine.
 */

import dayjs from 'dayjs';
import { parseTaskLine } from '@/domain/models/taskLine/taskLineParser';
import {
  setCompletionState,
  removeCompletionMark
} from '@/domain/models/taskLine/completionMark';
import { extractAttribute } from '@/domain/models/taskLine/taskLineExtractor';
import { injectAttribute } from '@/domain/models/taskLine/taskLineTransformer';
import { toLocalISODateInTimeZone } from '@/i12e/utilities/timeZoneToolkit';
import {
  nextOccurrenceOn,
  nextOccurrenceAt,
  parseRruleV1
} from '@/domain/models/recurrence/rruleV1';

const safeUuid = () => {
  const c = globalThis?.crypto;
  if (c?.randomUUID) return c.randomUUID();
  return `kairos_${Math.random().toString(36).slice(2)}_${Date.now()}`;
};

const normalizeScheduledOn = (value) => {
  const text = String(value || '').trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(text) ? text : null;
};

const normalizeScheduledAt = (value) => {
  const d = new Date(String(value || ''));
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

const stripDueFromDefinition = (definition) => {
  let updated = definition || '';
  // Recurrence spawns should pick an explicit due value for the new instance,
  // so we remove any "previous occurrence" due fields before injecting the
  // computed next one.
  ['due_on', 'due_at'].forEach((key) => {
    const { line } = extractAttribute(updated, key);
    updated = line;
  });
  return updated.trim();
};

const stripScheduledFromDefinition = (definition) => {
  let updated = definition || '';
  // Each instance must carry its own scheduled anchor/identity, so we remove
  // the previous instance scheduled values before injecting the next one.
  ['scheduled_on', 'scheduled_at'].forEach((key) => {
    const { line } = extractAttribute(updated, key);
    updated = line;
  });
  return updated.trim();
};

const parseStrictIsoDateOnly = (value) => {
  const d = dayjs(String(value || ''), 'YYYY-MM-DD', true);
  return d.isValid() ? d : null;
};

const maxIsoInstant = (aIso, bIso) => {
  // Recurrence progression uses `after = max(completed_at, scheduled_*)` so
  // early completion still advances the series beyond the scheduled instance.
  const a = new Date(String(aIso || '')).getTime();
  const b = new Date(String(bIso || '')).getTime();
  if (!Number.isFinite(a) && !Number.isFinite(b)) return null;
  if (!Number.isFinite(a)) return new Date(b).toISOString();
  if (!Number.isFinite(b)) return new Date(a).toISOString();
  return new Date(Math.max(a, b)).toISOString();
};

const maxIsoDateOnly = (aOn, bOn) => {
  // ISO8601 dates (YYYY-MM-DD) are lexicographically sortable.
  const a = normalizeScheduledOn(aOn);
  const b = normalizeScheduledOn(bOn);
  if (!a && !b) return null;
  if (!a) return b;
  if (!b) return a;
  return a >= b ? a : b;
};

const computeNextDueAttributes = ({ baseParsed, anchor, nextScheduled }) => {
  // Preserve user intent: if the user set a due offset relative to scheduled,
  // keep that same delta for future occurrences.
  //
  // If no due exists, default due to scheduled so spawned tasks bucket
  // predictably.
  if (nextScheduled.scheduled_on) {
    const scheduledOn = anchor.scheduled_on;
    const nextScheduledOn = nextScheduled.scheduled_on;
    if (baseParsed?.due_on) {
      const due = parseStrictIsoDateOnly(baseParsed.due_on);
      const scheduled = parseStrictIsoDateOnly(scheduledOn);
      const next = parseStrictIsoDateOnly(nextScheduledOn);
      if (due && scheduled && next) {
        const deltaDays = due.diff(scheduled, 'day');
        return { due_on: next.add(deltaDays, 'day').format('YYYY-MM-DD') };
      }
    }
    return { due_on: nextScheduledOn };
  }

  if (nextScheduled.scheduled_at) {
    const scheduledAt = anchor.scheduled_at;
    const nextScheduledAt = nextScheduled.scheduled_at;
    if (baseParsed?.due_at) {
      const dueMs = new Date(String(baseParsed.due_at)).getTime();
      const scheduledMs = new Date(String(scheduledAt)).getTime();
      const nextMs = new Date(String(nextScheduledAt)).getTime();
      if (
        Number.isFinite(dueMs) &&
        Number.isFinite(scheduledMs) &&
        Number.isFinite(nextMs)
      ) {
        const deltaMs = dueMs - scheduledMs;
        return { due_at: new Date(nextMs + deltaMs).toISOString() };
      }
    }
    return { due_at: nextScheduledAt };
  }

  return {};
};

const buildNextDefinition = ({
  baseDefinition,
  baseParsed,
  seriesId,
  nextScheduled,
  anchor
}) => {
  let nextDef = removeCompletionMark(baseDefinition);
  nextDef = injectAttribute(nextDef, 'series_id', seriesId);

  // Scheduled is both the per-instance identity and the recurrence anchor.
  nextDef = stripScheduledFromDefinition(nextDef);
  if (nextScheduled.scheduled_on) {
    nextDef = injectAttribute(
      nextDef,
      'scheduled_on',
      nextScheduled.scheduled_on
    );
  }
  if (nextScheduled.scheduled_at) {
    nextDef = injectAttribute(
      nextDef,
      'scheduled_at',
      nextScheduled.scheduled_at
    );
  }

  // Ensure the spawned instance has an explicit due for bucket visualization.
  const dueAttrs = computeNextDueAttributes({
    baseParsed,
    anchor,
    nextScheduled
  });
  nextDef = stripDueFromDefinition(nextDef);
  if (dueAttrs.due_on)
    nextDef = injectAttribute(nextDef, 'due_on', dueAttrs.due_on);
  if (dueAttrs.due_at)
    nextDef = injectAttribute(nextDef, 'due_at', dueAttrs.due_at);

  return nextDef;
};

const findBySeriesScheduled = (tasksMap, seriesId, scheduled) => {
  const tasks = Object.values(tasksMap || {});

  return (
    tasks.find((t) => {
      const parsed = parseTaskLine(t?.definition || '');
      if (String(parsed?.series_id || '') !== String(seriesId)) return false;

      if (scheduled.scheduled_on) {
        return (
          normalizeScheduledOn(parsed.scheduled_on) === scheduled.scheduled_on
        );
      }
      if (scheduled.scheduled_at) {
        return (
          normalizeScheduledAt(parsed.scheduled_at) === scheduled.scheduled_at
        );
      }
      return false;
    }) || null
  );
};

/**
 * @param {Object} deps
 * @param {Object} deps.taskService
 * @param {Object} deps.taskRepository
 * @param {string} deps.taskId
 * @param {string|null} deps.tzid
 * @param {string|null} deps.occurredAtIso
 * @returns {Promise<{ updatedTask: Object, spawnedTask: Object|null }>}
 */
export const toggleTaskCompletionWithRecurrence = ({
  taskService,
  taskRepository,
  taskId,
  tzid,
  occurredAtIso
}) => {
  const task = taskService.getTaskById(taskId);
  if (!task) {
    return Promise.reject(new Error(`Task with ID ${taskId} not found`));
  }

  const occurredAt = occurredAtIso || new Date().toISOString();
  const willBeCompleted = !task.completed_at;

  const parsed = parseTaskLine(task.definition);

  // A task is recurrence-enabled iff it has an rrule and a scheduled anchor.
  // We intentionally do not support `start_*` or `occurrence_*` in v1.
  const isRecurringCandidate =
    !!parsed.rrule && (!!parsed.scheduled_on || !!parsed.scheduled_at);

  const seriesId =
    isRecurringCandidate && parsed.series_id ? String(parsed.series_id) : null;
  const ensuredSeriesId =
    isRecurringCandidate && !seriesId ? safeUuid() : seriesId;
  const definitionWithSeries =
    isRecurringCandidate && !seriesId
      ? injectAttribute(task.definition, 'series_id', ensuredSeriesId)
      : task.definition;

  const updates = {
    completed_at: willBeCompleted ? occurredAt : null,
    definition: setCompletionState(definitionWithSeries, willBeCompleted)
  };

  return taskRepository.updateTask(taskId, updates).then((updatedTask) => {
    // Undo completion: no spawn side effects in v1.
    if (!willBeCompleted) return { updatedTask, spawnedTask: null };

    // Non-recurring tasks stop here (identical behavior to v0 toggle).
    if (!isRecurringCandidate) return { updatedTask, spawnedTask: null };

    const updatedParsed = parseTaskLine(definitionWithSeries);
    const hasAnchor =
      !!updatedParsed.scheduled_on || !!updatedParsed.scheduled_at;
    if (!updatedParsed.rrule || !hasAnchor)
      return { updatedTask, spawnedTask: null };

    const r = parseRruleV1(updatedParsed.rrule);
    if (!r.valid) return { updatedTask, spawnedTask: null };

    // Compute next scheduled anchor/identity.
    let nextScheduled = null;
    const anchor = {
      scheduled_on: updatedParsed.scheduled_on || null,
      scheduled_at: updatedParsed.scheduled_at || null
    };

    // Prefer datetime anchor when both exist.
    if (anchor.scheduled_at) {
      const afterAtIso = maxIsoInstant(occurredAt, anchor.scheduled_at);
      if (!afterAtIso) return { updatedTask, spawnedTask: null };
      nextScheduled = nextOccurrenceAt({
        rrule: updatedParsed.rrule,
        startAtIso: anchor.scheduled_at,
        afterAtIso
      });
    } else if (anchor.scheduled_on) {
      // Date-only anchors advance in *local date space*:
      // `afterOn = max(localDate(completed_at), scheduled_on)`.
      //
      // This avoids incorrect day shifts that occur when trying to turn
      // scheduled_on into a UTC instant without a timezone database.
      const occurredOn = toLocalISODateInTimeZone(occurredAt, tzid);
      const afterOn = maxIsoDateOnly(occurredOn, anchor.scheduled_on);
      if (!afterOn) return { updatedTask, spawnedTask: null };
      nextScheduled = nextOccurrenceOn({
        rrule: updatedParsed.rrule,
        startOn: anchor.scheduled_on,
        afterOn
      });
    }

    if (!nextScheduled) return { updatedTask, spawnedTask: null };

    // Client-side best-effort dedupe against local cache.
    return taskRepository.getAllTasks().then((tasks) => {
      const existing = findBySeriesScheduled(
        tasks,
        ensuredSeriesId,
        nextScheduled
      );
      if (existing) return { updatedTask, spawnedTask: existing };

      const nextDef = buildNextDefinition({
        baseDefinition: task.definition,
        baseParsed: parsed,
        seriesId: ensuredSeriesId,
        nextScheduled,
        anchor
      });

      return taskRepository
        .createTask(nextDef)
        .then((spawnedTask) => ({ updatedTask, spawnedTask }));
    });
  });
};
